import {
    FlexibleConnectedPositionStrategy,
    HorizontalConnectionPos,
    Overlay,
    OverlayConfig,
    OverlayRef,
    ScrollStrategy,
    VerticalConnectionPos
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    booleanAttribute,
    Directive,
    ElementRef,
    HostListener,
    inject,
    InjectionToken,
    Input,
    ViewContainerRef
} from '@angular/core';
import { merge, Subscription } from 'rxjs';
import { MenuPanel } from '../../menu.component';

export const MENU_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>(
    'menu-scroll-strategy',
    {
        providedIn: 'root',
        factory: () => {
            const overlay = inject(Overlay);
            return () => overlay.scrollStrategies.reposition();
        }
    }
);

export function MENU_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
    return () => overlay.scrollStrategies.reposition();
}

/** @docs-private */
export const MENU_SCROLL_STRATEGY_FACTORY_PROVIDER = {
    provide: MENU_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: MENU_SCROLL_STRATEGY_FACTORY
};

@Directive({
    selector: `[nwMenuTriggerFor]`,
    host: {
        '[attr.aria-haspopup]': 'menu ? "menu" : null',
        '[attr.aria-expanded]': 'menuOpen',
        '[attr.aria-controls]': 'menuOpen && menu ? menu.panelId : null',
        '(click)': 'handleClick()'
    },
    exportAs: 'nwMenuTrigger'
})
export class MenuTriggerDirective {
    @Input('nwMenuTriggerFor')
    get menu(): MenuPanel | null {
        return this._menu;
    }
    set menu(menu: MenuPanel | null) {
        if (menu === this._menu) {
            return;
        }

        this._menu = menu;
        this._menuCloseSubscription.unsubscribe();

        if (menu) {
            this._menuCloseSubscription = menu.close.subscribe(() => {
                this._destroyMenu();
            });
        }
    }

    @Input({ transform: booleanAttribute }) hasBackdrop: boolean = true;

    get menuOpen(): boolean {
        return this._menuOpen;
    }

    @HostListener('window:resize') resize(): void {
        if (!this._overlayRef) return;

        this._overlayRef.updateSize({
            minWidth: this._viewContainerRef.element.nativeElement.clientWidth || 200
        });
    }

    private _menu!: MenuPanel | null;
    private _menuOpen: boolean = false;
    private _menuCloseSubscription = Subscription.EMPTY;
    private _closingActionsSubscription = Subscription.EMPTY;
    private _portal!: TemplatePortal;
    private _overlayRef: OverlayRef | null = null;
    private _scrollStrategy: () => ScrollStrategy = inject(MENU_SCROLL_STRATEGY);
    private _overlay: Overlay = inject(Overlay);
    private _viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private _element: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

    public toggleMenu(): void {
        return this._menuOpen ? this.closeMenu() : this.openMenu();
    }

    public handleClick(): void {
        this.toggleMenu();
    }

    public openMenu(): void {
        const menu = this.menu;

        if (this._menuOpen || !menu) {
            return;
        }

        const overlayRef = this._createOverlay(menu);
        const overlayConfig = overlayRef.getConfig();
        const positionStrategy =
            overlayConfig.positionStrategy as FlexibleConnectedPositionStrategy;

        this._setPosition(menu, positionStrategy);
        overlayConfig.hasBackdrop = this.hasBackdrop;
        overlayRef.attach(this._getPortal(menu));

        this._closingActionsSubscription = this._menuClosingActions().subscribe(() =>
            this.closeMenu()
        );
        this._initMenu();
    }

    public closeMenu(): void {
        this._destroyMenu();
    }

    private _createOverlay(menu: MenuPanel): OverlayRef {
        if (!this._overlayRef) {
            const config = this._getOverlayConfig(menu);
            this._overlayRef = this._overlay.create(config);
            this._overlayRef.keydownEvents().subscribe();
        }

        return this._overlayRef;
    }

    private _setPosition(
        menu: MenuPanel,
        positionStrategy: FlexibleConnectedPositionStrategy
    ) {
        const [originX, originFallbackX]: HorizontalConnectionPos[] =
            menu.xPosition === 'before'
                ? ['end', 'start']
                : menu.xPosition === 'center'
                  ? ['center', 'center']
                  : ['start', 'end'];

        const [overlayY, overlayFallbackY]: VerticalConnectionPos[] =
            menu.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

        let [originY, originFallbackY] = [overlayY, overlayFallbackY];
        const [overlayX, overlayFallbackX] = [originX, originFallbackX];
        const offsetY = 0;

        originY = overlayY === 'top' ? 'bottom' : 'top';
        originFallbackY = overlayFallbackY === 'top' ? 'bottom' : 'top';

        positionStrategy.withPositions([
            { originX, originY, overlayX, overlayY, offsetY },
            {
                originX: originFallbackX,
                originY,
                overlayX: overlayFallbackX,
                overlayY,
                offsetY
            },
            {
                originX,
                originY: originFallbackY,
                overlayX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY
            },
            {
                originX: originFallbackX,
                originY: originFallbackY,
                overlayX: overlayFallbackX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY
            }
        ]);
    }

    private _getPortal(menu: MenuPanel): TemplatePortal {
        if (!this._portal || this._portal.templateRef !== menu.templateRef) {
            this._portal = new TemplatePortal(menu.templateRef, this._viewContainerRef);
        }

        return this._portal;
    }

    private _menuClosingActions() {
        return merge(this._overlayRef!.backdropClick(), this._overlayRef!.detachments());
    }

    private _getOverlayConfig(menu: MenuPanel): OverlayConfig {
        return new OverlayConfig({
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._element)
                .withLockedPosition()
                .withGrowAfterOpen()
                .withTransformOriginOn('.nw-menu-panel'),
            scrollStrategy: this._scrollStrategy(),
            minWidth: this._viewContainerRef.element.nativeElement.clientWidth || 200,
            panelClass: menu.panelClass,
            hasBackdrop: this.hasBackdrop
        });
    }

    private _initMenu(): void {
        this._setIsMenuOpen(true);
        this._menu?.opened.emit();
    }

    private _setIsMenuOpen(isOpen: boolean): void {
        if (isOpen !== this._menuOpen) {
            this._menuOpen = isOpen;
        }
    }

    private _destroyMenu() {
        if (!this._overlayRef || !this._menuOpen) {
            return;
        }

        this._closingActionsSubscription.unsubscribe();
        this._overlayRef.detach();

        this._setIsMenuOpen(false);
        this._menu?.close.emit();
    }
}
